import { memo } from "react";

import { isDrawingFormCategory, TicketingFormCategory } from "@simplyk/common";

import { RateContextProvider } from "./Context/RateContext";
import { AuctionRate } from "./editors/AuctionRate/AuctionRate";
import { CustomRate } from "./editors/CustomRate/CustomRate";
import { DrawingRate } from "./editors/DrawingRate/DrawingRate";
import { EventRate } from "./editors/EventRate/EventRate";
import { MembershipRate } from "./editors/MembershipRate/MembershipRate";
import { ShopRate } from "./editors/ShopRate/ShopRate";

import { TicketingRecurrenceOutput } from "@/common/types/trpc";

export interface RateProps {
  rateId: string;
  alreadyBoughtTicketsCount: number;
  rateIndex: number;
  formCategory: TicketingFormCategory;
  isEditing: boolean;
  isOnboarding: boolean;
  isRegistration: boolean;
  recurrences: TicketingRecurrenceOutput[] | undefined;
  hasMultipleDates: boolean;
  totalCardNumber: number;
  shouldDisplayDeleteButton?: boolean;
  shouldConfirmDelete?: boolean;
  onDelete: (index: number) => void;
  onDuplicate: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  /** Per-attendee questions the API will archive if this ticket type is deleted; named in its delete confirmation. */
  questionsArchivedOnDelete?: string[];
}

const RateInner = ({
  rateId,
  alreadyBoughtTicketsCount,
  rateIndex,
  formCategory,
  isEditing,
  isOnboarding,
  isRegistration,
  recurrences,
  hasMultipleDates,
  ...cardProps
}: RateProps) => {
  return (
    <RateContextProvider
      rateId={rateId}
      rateIndex={rateIndex}
      formCategory={formCategory}
      isEditing={isEditing}
      isOnboarding={isOnboarding}
      isRegistration={isRegistration}
      recurrences={recurrences}
      hasMultipleDates={hasMultipleDates}
      alreadyBoughtTicketsCount={alreadyBoughtTicketsCount}
    >
      {formCategory === TicketingFormCategory.Auction ? (
        <AuctionRate {...cardProps} />
      ) : formCategory === TicketingFormCategory.Shop ? (
        <ShopRate {...cardProps} />
      ) : formCategory === TicketingFormCategory.Custom ? (
        <CustomRate {...cardProps} />
      ) : formCategory === TicketingFormCategory.MembershipV2 ? (
        <MembershipRate {...cardProps} />
      ) : isDrawingFormCategory(formCategory) ? (
        <DrawingRate {...cardProps} />
      ) : (
        <EventRate {...cardProps} />
      )}
    </RateContextProvider>
  );
};

export const Rate = memo(RateInner);
Rate.displayName = "Rate";
